import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Icon } from '../components/Layout'
import VerifiedBadge from '../components/VerifiedBadge'
import { useToast } from '../context/ToastContext'
import { followUser, unfollowUser, checkFollowStatus, getProfile } from '../services/profiles'
import { getUserPosts, likePost, unlikePost } from '../services/posts'
import PostCard from '../components/PostCard'

export default function UserProfile() {
  const { userId } = useParams()
  const navigate = useNavigate()
  const [user, setUser] = useState(null)
  const [userPosts, setUserPosts] = useState([])
  const [following, setFollowing] = useState(false)
  const [followerCount, setFollowerCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [savingFollow, setSavingFollow] = useState(false)

  const [fetchError, setFetchError] = useState(null)

  const toast = useToast()

  useEffect(() => {
    setFetchError(null)
    Promise.all([
      getProfile(userId),
      getUserPosts(userId),
      checkFollowStatus(userId),
    ]).then(([profileData, posts, isAlreadyFollowing]) => {
      const p = profileData?.data ?? profileData ?? {}
      // Backend profile response shape (same as own profile):
      // p.userStat.cisScore.totalScore,
      // p.userStat.streak.currentStreak, p.userStat.postCount
      const rawCis    = p.userStat?.cisScore?.totalScore   ?? p.cisScore   ?? 0
      const rawStreak = p.userStat?.streak?.currentStreak  ?? p.streak     ?? 0
      const rawPosts  = p.userStat?.postCount              ?? p.postCount  ?? 0
      setUser({
        id:         p.id ?? p.userId ?? userId,
        profileId:  p.id,
        name:       p.fullName  || p.name  || 'Anonymous',
        handle:     p.username  ? `@${p.username}` : '',
        avatar:     p.avatarUrl || p.avatar || '',
        badge:      p.verifiedBadge ?? null,
        bio:        p.bio || '',
        website:    p.socialLinks?.website || '',
        followers:  p.followersCount ?? p.followers ?? 0,
        following:  p.followingCount ?? p.following ?? 0,
        cisScore:   rawCis,
        streak:     rawStreak,
        postCount:  rawPosts,
        cltHeld:    p.cltHeld ?? 0,
      })
      setFollowing(isAlreadyFollowing)
      setFollowerCount(p.followersCount ?? p.followers ?? 0)
      setUserPosts(Array.isArray(posts) ? posts : [])
    }).catch(err => {
      setFetchError(err.message)
      setUser(null)
    })
      .finally(() => setLoading(false))
  }, [userId])

  const handleFollowToggle = async () => {
    if (!user || savingFollow) return

    const nextFollowing = !following
    const nextFollowerCount = followerCount + (nextFollowing ? 1 : -1)

    setFollowing(nextFollowing)
    setFollowerCount(nextFollowerCount)
    setSavingFollow(true)

    try {
      if (nextFollowing) await followUser(userId)
      else await unfollowUser(userId)
    } catch (err) {
      const is400 = err?.status === 400 || err?.message?.includes('400')
      if (is400) {
        // 400 = already in that follow state — trust server, set following=true
        setFollowing(true)
      } else {
        setFollowing(!nextFollowing)
        setFollowerCount(followerCount)
        toast.error(err?.message || `Could not ${nextFollowing ? 'follow' : 'unfollow'} this user.`)
      }
    } finally {
      setSavingFollow(false)
    }
  }

  if (loading) {
    return (
      <div className="flex-1 lg:ml-[300px] w-full max-w-3xl flex items-center justify-center py-20">
        <div className="w-8 h-8 border-[3px] border-on-background border-t-transparent animate-spin" />
      </div>
    )
  }

  if (!user) {
    return (
      <div className="flex-1 lg:ml-[300px] w-full max-w-3xl flex flex-col items-center justify-center gap-4 py-20">
        <span className="material-symbols-outlined text-[48px] text-on-surface-variant opacity-30">person_off</span>
        <p className="text-on-surface-variant font-bold uppercase tracking-widest text-sm">
          {fetchError ? 'Failed to load profile' : 'User not found'}
        </p>
        <button onClick={() => navigate('/')} className="px-4 py-2 bg-primary-container text-on-primary-fixed font-bold text-sm uppercase border border-on-background/20 lg:neo-border">
          Back to Feed
        </button>
      </div>
    )
  }

  const isOwnProfile = false // always viewing another user's profile from this route

  return (
    <div className="flex-1 lg:ml-[300px] lg:mr-[340px] w-full max-w-3xl flex flex-col gap-4 lg:gap-8">
      {/* Cover + Avatar */}
      <section>
        <div className="w-full h-28 lg:h-40 bg-primary-container border border-on-background/10 lg:neo-border lg:neo-shadow" />
        <div className="bg-surface-container border border-on-background/10 border-t-0 lg:neo-border lg:border-t-0 p-3 lg:p-6">
          <div className="flex justify-between items-start mb-3 lg:mb-4">
            <div className="-mt-10 lg:-mt-16 w-16 h-16 lg:w-24 lg:h-24 border-2 border-on-background/20 lg:neo-border overflow-hidden">
              <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
            </div>

            {/* Follow / Unfollow button */}
            {!isOwnProfile && (
              <button
                disabled={savingFollow}
                onClick={handleFollowToggle}
                className={`mt-2 flex items-center gap-2 px-4 lg:px-6 py-2 lg:py-2.5 font-bold text-[12px] lg:text-[13px] uppercase tracking-wider transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed
                  ${following
                    ? 'bg-surface-container text-on-background border border-on-background/30 lg:neo-border hover:bg-error/10 hover:text-error hover:border-error/40'
                    : 'bg-primary-container text-on-primary-fixed border border-on-background/20 lg:neo-border lg:neo-shadow-sm hover:brightness-105'
                  }`}
              >
                <Icon name={following ? 'person_remove' : 'person_add'} className="text-[16px]" />
                {following ? 'Following' : 'Follow'}
              </button>
            )}
          </div>

          <h1 className="font-extrabold uppercase tracking-tight leading-none text-[20px] lg:text-[28px] mb-1 flex items-center gap-2">
            {user.name}
            <VerifiedBadge badge={user.badge} size={18} />
          </h1>
          <p className="text-[11px] lg:text-[12px] font-mono text-on-surface-variant mb-2 lg:mb-3">{user.handle}</p>
          <p className="text-sm lg:text-body-md text-on-surface-variant max-w-xl mb-3 lg:mb-4">{user.bio}</p>

          {user.website && (
            <div className="flex items-center gap-2 text-[11px] lg:text-[12px] font-mono text-primary-container mb-3 lg:mb-4">
              <Icon name="link" className="text-[14px] lg:text-[16px]" />
              <a href={user.website} target="_blank" rel="noopener noreferrer">{user.website}</a>
            </div>
          )}

          {/* Stats */}
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 w-full mt-3 lg:mt-4 pt-3 lg:pt-4 border-t border-on-background/10">
            {[
              { label: 'Posts',     value: (user.postCount ?? userPosts.length).toLocaleString() },
              { label: 'Followers', value: followerCount >= 1000 ? `${(followerCount / 1000).toFixed(1)}k` : followerCount },
              { label: 'Following', value: user.following ?? 0 },
              { label: 'CIS',       value: Number(user.cisScore ?? 0).toLocaleString(), accent: true },
              { label: 'Streak',    value: `${user.streak ?? 0}d` },
              // TODO: backend doesn't yet expose CPT for non-self profiles
              // (/wallet only resolves the logged-in user). Stubbed at 0
              // until a profile-scoped CPT field/endpoint exists.
              { label: 'CPT',       value: '0', accent: true },
              { label: 'CLT',       value: Number(user.cltHeld ?? 0).toLocaleString() },
            ].map((stat) => (
              <div key={stat.label} className="flex flex-col items-center border border-on-background/10 bg-background/60 px-2 py-2 lg:py-3">
                <span className="text-[9px] lg:text-[10px] uppercase text-on-surface-variant font-bold tracking-wide">{stat.label}</span>
                <span className={`text-sm lg:text-base font-black mt-0.5 ${stat.accent ? 'text-primary-container' : ''}`}>
                  {stat.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Posts */}
      <section className="bg-surface-container border border-on-background/10 lg:neo-border lg:neo-shadow p-3 lg:p-6">
        <div className="flex justify-between items-center mb-3 lg:mb-6">
          <h2 className="font-headline-md text-sm lg:text-headline-md uppercase font-black">Posts</h2>
          <Icon name="sort" className="cursor-pointer" />
        </div>
        {userPosts.length === 0 ? (
          <p className="text-on-surface-variant py-6 lg:py-8 text-center text-sm">No posts yet.</p>
        ) : (
          <div className="flex flex-col gap-3 lg:gap-6">
            {userPosts.map((post) => (
              <PostCard
                key={post.id}
                post={post}
                onLike={async (postId) => {
                  const p = userPosts.find(x => x.id === postId)
                  if (!p) return
                  setUserPosts(prev => prev.map(x =>
                    x.id === postId
                      ? { ...x, liked: !x.liked, likes: x.liked ? (Number(x.likes)||0)-1 : (Number(x.likes)||0)+1 }
                      : x
                  ))
                  try {
                    if (p.liked) await unlikePost(postId)
                    else await likePost(postId)
                  } catch {
                    setUserPosts(prev => prev.map(x => x.id === postId ? { ...x, liked: p.liked, likes: p.likes } : x))
                  }
                }}
              />
            ))}
          </div>
        )}
      </section>

      <div className="h-20 md:h-0" />
    </div>
  )
}